import { useId, useMemo, useRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import {
  addressLines,
  countryOf,
  fold,
  hasRegion,
  postalExampleOf,
  postalKindOf,
  postalRequired,
  regionLabelOf,
  regionRequired,
  tidyAddress,
  type AddressProblemField,
  type CountryEntry,
} from '@/domain';
import { useAreaSearch, useCitySearch, useRegions } from '@/application/usecases/useGeo';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { strings } from '@/shared/strings';
import { AddressBlock } from '@/ui/data/AddressBlock';
import { SkeletonBar } from '@/ui/feedback/Loading';
import { Button } from '@/ui/primitives/Button';
import { Combobox, type ComboboxOption } from '@/ui/primitives/Combobox';
import { modalStyles } from '@/ui/primitives/Modal';
import { addressEmpty, cleared, inCountry, partOf, type AddressParts } from './addressParts';
import styles from './AddressFields.module.css';

export interface AddressFieldsProps {
  value: AddressParts;
  onChange: (next: AddressParts) => void;
  /** The country list: this draws nothing useful without it, so its owner waits for it. */
  countries: readonly CountryEntry[];
  /** What is wrong with each part, by the key the server names it with. */
  errors: Partial<Record<AddressProblemField, string>>;
  /** Whom the address is for, above the label it previews. */
  name: string;
  clinic: string;
  /** Where the form finds each control, to put the focus on the first that is wrong. */
  register: (field: AddressProblemField) => (element: HTMLElement | null) => void;
}

/** A city's row is told from another by its id, and one typed by hand, which has none, by its name. */
const TYPED_CITY = 'typed:';

/**
 * The parts of a doctor's address, each asked for as its country asks for
 * it. The request of 6 October 2026 (fifth), section 2.
 *
 *   country   chosen from all 250, searched as it is typed.
 *   region    only where the country's addresses have one, under the word
 *             the country uses (State, Province, County); chosen from the
 *             country's regions or typed.
 *   city      suggested from the country's cities while it is typed, and
 *             from the region's once one is chosen; a city the list does not
 *             have is typed and used as typed.
 *   area      suggested from the areas doctors of that city already have.
 *   lines     two of them, typed.
 *   postal    a postal code, a ZIP code, a PIN code or an Eircode by the
 *             country, held to its pattern and shown one that fits; a PO box
 *             where the country has no postal code.
 *
 * Changing the country clears the region, the city, the area and the postal
 * code, so no part is kept from a country it does not belong to. Choosing a
 * city the list knows the region of sets the region; choosing another region
 * lets the city go. Under the fields is the label as that country's post
 * writes it, drawn from what is typed, by the rule the server writes it with.
 *
 * An address is all or nothing, so nothing is marked as required until some
 * part is filled in; from then on the parts the country needs are marked.
 * What is wrong with a part is the form's to say, through `errors`.
 */
export function AddressFields({
  value,
  onChange,
  countries,
  errors,
  name,
  clinic,
  register,
}: AddressFieldsProps) {
  const base = useId();
  const idOf = (part: string) => `${base}-${part}`;

  const country = countryOf(countries, value.country);
  const regional = hasRegion(country);
  const started = !addressEmpty(value);

  // Each list is asked for when its field is first opened, not when the form is.
  const [asked, setAsked] = useState({ region: false, city: false, area: false });
  const ask = (list: keyof typeof asked) => () =>
    setAsked((current) => (current[list] ? current : { ...current, [list]: true }));
  // The city the field held when it was opened: choosing another one lets the area go.
  const cityBefore = useRef(value.city);

  const regions = useRegions(regional && asked.region ? value.country : null);
  const cityText = value.city ?? '';
  const cities = useCitySearch(
    asked.city ? value.country : null,
    regional ? value.regionCode : null,
    cityText,
  );
  const areaText = value.area ?? '';
  // The areas are a city's, and the city is being typed: asked for once the typing has paused.
  const areasCity = useDebouncedValue(cityText);
  const areas = useAreaSearch(asked.area ? value.country : null, areasCity, areaText);

  const countryOptions = useMemo<ComboboxOption[]>(
    () =>
      countries.map((each) => ({ key: each.code, label: each.name, alsoFoundBy: [each.code] })),
    [countries],
  );
  const regionOptions = (regions.data ?? []).map((each) => ({ key: each.code, label: each.name }));
  const cityOptions = (cities.data ?? []).map((each) => ({
    key: each.id ?? `${TYPED_CITY}${each.name}`,
    label: each.name,
    // Which of two cities of one name: the region each is in. Not said of a city named as its region is.
    detail: fold(each.regionName) === fold(each.name) ? null : each.regionName,
  }));
  const areaOptions = (areas.data ?? []).map((each) => ({ key: each.name, label: each.name }));

  // The list answers exactly what is in the box: only then is "Use what I typed" an honest offer.
  const citiesAnswer = cities.data !== undefined && cities.answeredFor === cityText.trim();
  const areasAnswer = areas.data !== undefined && areas.answeredFor === areaText.trim();

  function chooseCity(option: ComboboxOption) {
    const city = cities.data?.find(
      (each) => (each.id ?? `${TYPED_CITY}${each.name}`) === option.key,
    );
    if (city === undefined) return;

    const moved = fold(city.name) !== fold(cityBefore.current);
    cityBefore.current = city.name;
    onChange({
      ...value,
      city: city.name,
      cityId: city.id,
      // A city the list knows the region of brings its region with it.
      ...(regional && city.regionCode !== null
        ? { region: city.regionName, regionCode: city.regionCode }
        : {}),
      area: moved ? null : value.area,
    });
  }

  /** One of the three parts that are a line of text and nothing more. */
  function line(
    field: AddressProblemField,
    part: 'line1' | 'line2' | 'postalCode',
    label: string,
    hint: string | null,
    required: boolean,
    input: InputHTMLAttributes<HTMLInputElement> = {},
  ): ReactNode {
    const id = idOf(part);
    const error = errors[field];
    const described = [error !== undefined ? `${id}-error` : '', hint !== null ? `${id}-hint` : '']
      .filter((each) => each !== '')
      .join(' ');

    return (
      <div className={modalStyles.field}>
        <label
          className={`${modalStyles.label} ${required ? styles.required : ''}`}
          htmlFor={id}
        >
          {label}
        </label>
        <input
          {...input}
          ref={register(field)}
          id={id}
          className={modalStyles.input}
          autoComplete="off"
          value={value[part] ?? ''}
          aria-required={required || undefined}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={described === '' ? undefined : described}
          onChange={(event) => onChange({ ...value, [part]: partOf(event.target.value) })}
        />
        {error !== undefined && (
          <p id={`${id}-error`} className={modalStyles.error}>
            {error}
          </p>
        )}
        {hint !== null && (
          <span id={`${id}-hint`} className={modalStyles.hint}>
            {hint}
          </span>
        )}
      </div>
    );
  }

  const regionLabel = regionLabelOf(country);
  const postalKind = postalKindOf(country);
  const example = postalExampleOf(country);
  const labelLines =
    country === undefined ? [] : addressLines(tidyAddress(value, country), country);

  return (
    <>
      <Combobox
        id={idOf('country')}
        label={strings.address.country}
        value={country?.name ?? ''}
        options={countryOptions}
        required={started}
        error={errors['address.country'] ?? null}
        words={strings.address.list}
        autoCapitalize="words"
        inputRef={register('address.country')}
        onChoose={(option) => {
          if (option.key !== value.country) onChange(inCountry(value, option.key));
        }}
      />

      {regional && regionLabel !== null && (
        <Combobox
          id={idOf('region')}
          label={strings.address.region[regionLabel]}
          value={value.region ?? ''}
          options={regionOptions}
          loading={regions.isPending}
          offerTyped={regions.data !== undefined}
          required={started && regionRequired(country)}
          error={errors['address.region'] ?? null}
          words={strings.address.list}
          autoCapitalize="words"
          inputRef={register('address.region')}
          onOpen={ask('region')}
          // Typed and not chosen: the words stand for themselves, with no code.
          onType={(text) => onChange({ ...value, region: partOf(text), regionCode: null })}
          onChoose={(option) =>
            onChange(
              option.key === value.regionCode
                ? { ...value, region: option.label }
                : // Another region: the city chosen in the last one is not in this one.
                  { ...value, region: option.label, regionCode: option.key, city: null, cityId: null, area: null },
            )
          }
          onClear={() => onChange({ ...value, region: null, regionCode: null })}
        />
      )}

      <Combobox
        id={idOf('city')}
        label={strings.address.city}
        value={cityText}
        options={cityOptions}
        fetched
        loading={!citiesAnswer}
        offerTyped={citiesAnswer}
        required={started}
        error={errors['address.city'] ?? null}
        hint={strings.address.typedHint}
        words={strings.address.list}
        autoCapitalize="words"
        inputRef={register('address.city')}
        onOpen={() => {
          cityBefore.current = value.city;
          ask('city')();
        }}
        // Typed over: no longer the city of the list it was chosen as.
        onType={(text) => onChange({ ...value, city: partOf(text), cityId: null })}
        onChoose={chooseCity}
        onUseTyped={(text) => {
          const moved = fold(text) !== fold(cityBefore.current);
          cityBefore.current = text;
          if (moved && value.area !== null) onChange({ ...value, area: null });
        }}
        onClear={() => onChange({ ...value, city: null, cityId: null, area: null })}
      />

      <Combobox
        id={idOf('area')}
        label={strings.address.area}
        value={areaText}
        options={areaOptions}
        fetched
        loading={cityText.trim() !== '' && !areasAnswer}
        offerTyped={areasAnswer}
        error={errors['address.area'] ?? null}
        words={strings.address.list}
        autoCapitalize="words"
        inputRef={register('address.area')}
        onOpen={ask('area')}
        onType={(text) => onChange({ ...value, area: partOf(text) })}
        onChoose={(option) => onChange({ ...value, area: option.label })}
        onClear={() => onChange({ ...value, area: null })}
      />

      {line('address.line1', 'line1', strings.address.line1, strings.address.line1Hint, started, {
        autoCapitalize: 'words',
      })}
      {line('address.line2', 'line2', strings.address.line2, strings.address.line2Hint, false, {
        autoCapitalize: 'words',
      })}
      {line(
        'address.postalCode',
        'postalCode',
        strings.address.postal[postalKind],
        postalKind === 'pobox'
          ? strings.address.poBoxHint
          : example === null
            ? null
            : strings.address.example(example),
        started && postalRequired(country),
        { autoCapitalize: 'characters', spellCheck: false },
      )}

      {started && (
        <div className={styles.preview}>
          <span className={modalStyles.label}>{strings.address.preview}</span>
          <AddressBlock className={styles.label} name={name} clinic={clinic} lines={labelLines} />
          <Button variant="text" className={styles.clear} onClick={() => onChange(cleared(value))}>
            {strings.address.clear}
          </Button>
        </div>
      )}
    </>
  );
}

/**
 * Where the address fields will be, for the moment the country list takes to
 * arrive: the shape of the fields and nothing to type in. The fields above it
 * in the form are there from the start, and nothing above this moves when the
 * list comes, because the address is the last thing in the form.
 */
export function AddressFieldsLoading() {
  return (
    <div role="status" aria-live="polite">
      <span className="visually-hidden">{strings.address.loading}</span>
      <div className={styles.loading} aria-hidden="true">
        {['28%', '22%', '34%', '38%', '38%', '26%'].map((width, index) => (
          <div key={index} className={styles.loadingField}>
            <SkeletonBar width={width} height={10} />
            <SkeletonBar width="100%" height={44} />
          </div>
        ))}
      </div>
    </div>
  );
}
